import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, formatMXN } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Store, CheckCircle2, XCircle, HandCoins, TrendingUp, Wallet, Clock, Trophy, Users, Lock, Printer, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import PromoterRanking from "../components/PromoterRanking";
import PromoterTeam from "../components/PromoterTeam";
import PromoterPoster from "../components/PromoterPoster";

function monthLabel(s) {
  if (!s) return "—";
  const [y, m] = s.split("-");
  const names = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

export default function PromoterDashboard() {
  const [me, setMe] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [commissions, setCommissions] = useState([]);

  useEffect(() => {
    api.get("/promoter/me").then((r) => setMe(r.data)).catch((e) => console.warn("promoter/me", e?.message));
    api.get("/promoter/businesses").then((r) => setBusinesses(r.data || [])).catch((e) => console.warn("promoter/businesses", e?.message));
    api.get("/promoter/commissions").then((r) => setCommissions(r.data || [])).catch((e) => console.warn("promoter/commissions", e?.message));
  }, []);

  const stats = me?.stats || {};

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="overline text-[hsl(var(--primary))]">Panel de promotor</div>
        <h1 className="font-display text-4xl tracking-tight mt-2">Hola, {me?.name || "promotor"}</h1>
        <div className="text-sm text-muted-foreground mt-1">Tel: {me?.phone || "—"} · Comisión por pago: {formatMXN(me?.commission_per_payment || 100)}</div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="promoter-stats">
          <Stat icon={<Store className="w-5 h-5" />} label="Comercios afiliados" value={stats.total_businesses ?? 0} />
          <Stat icon={<CheckCircle2 className="w-5 h-5" />} label="Pagando ahora" value={stats.active_businesses ?? 0} highlight />
          <Stat icon={<XCircle className="w-5 h-5" />} label="Inactivos" value={stats.inactive_businesses ?? 0} />
          <Stat icon={<TrendingUp className="w-5 h-5" />} label="Ganancia este mes" value={formatMXN(stats.this_month_earnings || 0)} highlight />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          <Stat icon={<Wallet className="w-5 h-5" />} label="Total ganado (histórico)" value={formatMXN(stats.total_earned || 0)} />
          <Stat icon={<Clock className="w-5 h-5" />} label="Por cobrar" value={formatMXN(stats.pending_amount || 0)} />
          <Stat icon={<HandCoins className="w-5 h-5" />} label="Ya cobrado" value={formatMXN(stats.paid_amount || 0)} />
        </div>

        <Tabs defaultValue="businesses" className="mt-10">
          <TabsList className="bg-white border border-border" data-testid="promoter-tabs">
            <TabsTrigger value="businesses" data-testid="promoter-tab-businesses">Mis comercios</TabsTrigger>
            <TabsTrigger value="commissions" data-testid="promoter-tab-commissions">Comisiones</TabsTrigger>
            <TabsTrigger value="team" data-testid="promoter-tab-team"><Users className="w-3.5 h-3.5 mr-1.5" />Mi equipo</TabsTrigger>
            <TabsTrigger value="ranking" data-testid="promoter-tab-ranking"><Trophy className="w-3.5 h-3.5 mr-1.5" />Ranking</TabsTrigger>
            <TabsTrigger value="poster" data-testid="promoter-tab-poster"><Printer className="w-3.5 h-3.5 mr-1.5" />Mi cartel</TabsTrigger>
            <TabsTrigger value="cuenta" data-testid="promoter-tab-cuenta"><CreditCard className="w-3.5 h-3.5 mr-1.5" />Mi cuenta</TabsTrigger>
          </TabsList>

          <TabsContent value="businesses" className="mt-6">
            <div className="bg-white border border-border rounded-2xl p-6">
              <h3 className="font-display text-2xl tracking-tight">Comercios que afiliaste</h3>
              <p className="text-sm text-muted-foreground mt-1">Para ligar un comercio a tu nombre, dile que ponga "{me?.name}" en el campo "Nombre de su promotor" al registrarse.</p>

              {businesses.length === 0 ? (
                <div className="mt-6 text-center text-sm text-muted-foreground py-10 border-2 border-dashed border-border rounded-xl" data-testid="promoter-no-businesses">
                  Aún no tienes comercios afiliados. ¡Invita al primero!
                </div>
              ) : (
                <div className="overflow-x-auto mt-5">
                  <table className="w-full text-sm">
                    <thead className="text-left text-muted-foreground border-b border-border">
                      <tr><th className="py-2">Comercio</th><th>Giro</th><th>CP</th><th>Suscripción</th><th>Último pago</th></tr>
                    </thead>
                    <tbody>
                      {businesses.map((b) => (
                        <tr key={b.id} className="border-b border-border" data-testid={`promoter-biz-row-${b.id}`}>
                          <td className="py-3">
                            <div className="font-medium">{b.business_name}</div>
                            <div className="text-xs text-muted-foreground font-mono">{b.whatsapp}</div>
                          </td>
                          <td>{b.giro}</td>
                          <td>{b.postal_code}</td>
                          <td>
                            {b.subscription_active ? (
                              <Badge className="bg-[#7C3AED] text-white">Pagando</Badge>
                            ) : (
                              <Badge variant="outline" className="text-rose-600 border-rose-200">Inactivo</Badge>
                            )}
                            {b.subscription_expires_at && (
                              <div className="text-[10px] text-muted-foreground mt-1">
                                {b.subscription_active ? "vence" : "venció"} {new Date(b.subscription_expires_at).toLocaleDateString("es-MX")}
                              </div>
                            )}
                          </td>
                          <td>
                            {b.last_payment_at ? (
                              <>
                                <div>{formatMXN(b.last_payment_amount || 0)}</div>
                                <div className="text-[10px] text-muted-foreground">{new Date(b.last_payment_at).toLocaleDateString("es-MX")}</div>
                              </>
                            ) : <span className="text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="commissions" className="mt-6">
            <div className="bg-white border border-border rounded-2xl p-6">
              <h3 className="font-display text-2xl tracking-tight">Mis comisiones</h3>
              <p className="text-sm text-muted-foreground mt-1">Cada vez que un comercio afiliado paga su cuota mensual, se genera una comisión a tu favor.</p>

              {commissions.length === 0 ? (
                <div className="mt-6 text-center text-sm text-muted-foreground py-10 border-2 border-dashed border-border rounded-xl" data-testid="promoter-no-commissions">
                  Aún no hay comisiones. Aparecerán aquí cuando tus comercios paguen.
                </div>
              ) : (
                <div className="overflow-x-auto mt-5">
                  <table className="w-full text-sm">
                    <thead className="text-left text-muted-foreground border-b border-border">
                      <tr><th className="py-2">Mes</th><th>Comercio</th><th>Monto</th><th>Estado</th><th>Fecha</th></tr>
                    </thead>
                    <tbody>
                      {commissions.map((c) => (
                        <tr key={c.id} className="border-b border-border" data-testid={`promoter-comm-row-${c.id}`}>
                          <td className="py-3 font-medium">{monthLabel(c.month)}</td>
                          <td>{c.business_name}</td>
                          <td className="font-medium text-emerald-700">{formatMXN(c.amount)}</td>
                          <td>
                            {c.status === "paid" ? (
                              <Badge className="bg-emerald-500 text-white">Cobrado</Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-600 border-amber-200">Por cobrar</Badge>
                            )}
                          </td>
                          <td className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString("es-MX")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="ranking" className="mt-6">
            <PromoterRanking mode="promoter" />
          </TabsContent>

          <TabsContent value="team" className="mt-6">
            <PromoterTeam />
          </TabsContent>

          <TabsContent value="poster" className="mt-6">
            <PromoterPoster promoter={me} />
          </TabsContent>

          <TabsContent value="cuenta" className="mt-6 space-y-6">
            <PromoterBankAccount promoter={me} onSaved={setMe} />
            <PromoterChangePassword />
          </TabsContent>
        </Tabs>
      </section>
      <Footer />
    </div>
  );
}

function PromoterChangePassword() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (form.new_password !== form.confirm) { toast.error("Las contraseñas no coinciden"); return; }
    if (form.new_password.length < 6) { toast.error("Mínimo 6 caracteres"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Contraseña actualizada");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar contraseña");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-8 bg-white border border-border rounded-2xl p-6 max-w-md space-y-4" data-testid="promoter-change-password">
      <div className="flex items-center gap-2">
        <Lock className="w-4 h-4 text-muted-foreground" />
        <h3 className="font-semibold text-lg">Cambiar contraseña</h3>
      </div>
      <div><Label className="text-sm">Contraseña actual</Label><Input type="password" className="mt-1.5" value={form.current_password} onChange={(e) => set("current_password", e.target.value)} data-testid="promoter-pwd-current" /></div>
      <div><Label className="text-sm">Nueva contraseña</Label><Input type="password" className="mt-1.5" value={form.new_password} onChange={(e) => set("new_password", e.target.value)} data-testid="promoter-pwd-new" /></div>
      <div><Label className="text-sm">Confirmar nueva contraseña</Label><Input type="password" className="mt-1.5" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} data-testid="promoter-pwd-confirm" /></div>
      <Button onClick={save} disabled={busy || !form.current_password || !form.new_password} className="rounded-full px-6" data-testid="promoter-pwd-save-btn">
        {busy ? "Guardando…" : "Cambiar contraseña"}
      </Button>
    </div>
  );
}

function PromoterBankAccount({ promoter, onSaved }) {
  const existing = promoter?.payment_account || {};
  const [form, setForm] = useState({
    bank_name: existing.bank_name || "",
    account_number: existing.account_number || "",
    account_holder: existing.account_holder || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Sync when promoter loads
  React.useEffect(() => {
    if (promoter?.payment_account) {
      setForm({
        bank_name: promoter.payment_account.bank_name || "",
        account_number: promoter.payment_account.account_number || "",
        account_holder: promoter.payment_account.account_holder || "",
      });
    }
  }, [promoter]);

  const save = async () => {
    if (!form.bank_name.trim() || !form.account_number.trim() || !form.account_holder.trim()) {
      toast.error("Completa todos los campos");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/promoter/me/payment-account", form);
      onSaved(data);
      toast.success("Cuenta de cobro guardada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-6 max-w-lg space-y-5" data-testid="promoter-bank-account">
      <div className="flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-muted-foreground" />
        <h3 className="font-display text-2xl tracking-tight">Mi cuenta de cobro</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Los comercios que afiliaste depositarán tu comisión directamente en esta cuenta. Mantén tus datos actualizados.
      </p>

      {existing.updated_at && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-800">
          Cuenta registrada · última actualización {new Date(existing.updated_at).toLocaleDateString("es-MX")}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <Label className="text-sm">Banco</Label>
          <Input
            className="mt-1.5"
            placeholder="Ej. BBVA, HSBC, Banorte…"
            value={form.bank_name}
            onChange={(e) => set("bank_name", e.target.value)}
            data-testid="bank-name-input"
          />
        </div>
        <div>
          <Label className="text-sm">Número de cuenta / CLABE</Label>
          <Input
            className="mt-1.5"
            placeholder="18 dígitos CLABE o número de cuenta"
            value={form.account_number}
            onChange={(e) => set("account_number", e.target.value)}
            data-testid="account-number-input"
          />
        </div>
        <div>
          <Label className="text-sm">Nombre del titular</Label>
          <Input
            className="mt-1.5"
            placeholder="Nombre completo como aparece en tu cuenta"
            value={form.account_holder}
            onChange={(e) => set("account_holder", e.target.value)}
            data-testid="account-holder-input"
          />
        </div>
      </div>

      <Button
        onClick={save}
        disabled={busy}
        className="btn-primary rounded-full px-6"
        data-testid="save-bank-account-btn"
      >
        {busy ? "Guardando…" : "Guardar cuenta"}
      </Button>
    </div>
  );
}

const Stat = ({ icon, label, value, highlight }) => (
  <div className={`rounded-2xl border p-5 ${highlight ? "bg-[#F5F3FF] border-[#7C3AED]/30" : "bg-white border-border"}`}>
    <div className="flex items-center gap-2 text-muted-foreground">
      {icon}
      <span className="overline text-xs">{label}</span>
    </div>
    <div className="font-display text-3xl mt-2">{value}</div>
  </div>
);
